import { inject, Injectable } from '@angular/core';
import { IAutomobilePlatformApiService } from './automobile-platform-api.interface';
import { environment } from '@env/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  AutomobilePlatform,
  AutomobilePlatformLite,
} from '../domain/AutomobilePlatform.model';
import {
  AutomobilePlatformApiModel,
  AutomobilePlatformLiteApiModel,
} from './models/automobile-platform-api.model';
import { map, Observable, take } from 'rxjs';
import { ResponseApi } from '@domain/shared/infrastructure/models/response-api.interface';
import { AutomobilePlatformCreateOrUpdate } from '../domain/AutomobilePlatformCreateOrUpdate.model';
import { SetMapAutomobilePlatformsLite } from '../mappers/automobile-platform-lite.mapper';
import { SetMapAutomobilePlatform } from '../mappers/automobile-platform.mapper';

@Injectable({
  providedIn: 'root',
})
export class AutomobilePlatformApiService
  implements IAutomobilePlatformApiService
{
  private readonly BASE_URL = environment.baseUrl;
  private _http = inject(HttpClient);

  getAllAutomobilePlatforms(): Observable<AutomobilePlatformLite[]> {
    const url = `${this.BASE_URL}automobile-platform`;
    return this._http.get<ResponseApi>(url).pipe(
      take(1),
      map((response) => {
        if (!response.ok) return [];
        return SetMapAutomobilePlatformsLite(
          response.results as AutomobilePlatformLiteApiModel[]
        );
      })
    );
  }

  getAutomobilePlatformById(id: number): Observable<AutomobilePlatform | null> {
    const url = `${this.BASE_URL}automobile-platform/${id}`;
    return this._http.get<ResponseApi>(url).pipe(
      take(1),
      map((response) => {
        if (!response.ok) return null;
        return SetMapAutomobilePlatform(
          response.results as AutomobilePlatformApiModel
        );
      })
    );
  }

  getAutomobilePlatformByActivityId(
    activityID: number
  ): Observable<AutomobilePlatformLite[]> {
    const url = `${this.BASE_URL}automobile-platform/activity/${activityID}`;
    return this._http.get<ResponseApi>(url).pipe(
      take(1),
      map((response) => {
        if (!response.results) return [];
        if (Array.isArray(response.results) && response.results.length == 0)
          return response.results;

        return SetMapAutomobilePlatformsLite(
          response.results as AutomobilePlatformLiteApiModel[]
        );
      })
    );
  }

  createAutomobilePlatform(
    activityID: number,
    createAutomobilePlatform: AutomobilePlatformCreateOrUpdate
  ): Observable<AutomobilePlatform | null> {
    const url = `${this.BASE_URL}automobile-platform/${activityID}`;
    const headers = new HttpHeaders();
    return this._http
      .post<ResponseApi>(url, createAutomobilePlatform, { headers })
      .pipe(
        take(1),
        map((response) => {
          if (!response.ok) return null;
          return SetMapAutomobilePlatform(
            response.results as AutomobilePlatformApiModel
          );
        })
      );
  }

  updateAutomobilePlatform(
    id: number,
    updateAutomobilePlatform: AutomobilePlatformCreateOrUpdate
  ): Observable<AutomobilePlatform | null> {
    const url = `${this.BASE_URL}automobile-platform/${id}`;
    const headers = new HttpHeaders();
    return this._http
      .put<ResponseApi>(url, updateAutomobilePlatform, { headers })
      .pipe(
        take(1),
        map((response) => {
          if (!response.ok) return null;
          return SetMapAutomobilePlatform(
            response.results as AutomobilePlatformApiModel
          );
        })
      );
  }

  updateAutomobilePlatformDownload(
    id: number,
  ): Observable<AutomobilePlatform | null> {
    const url = `${this.BASE_URL}automobile-platform/download/${id}`;
    const headers = new HttpHeaders();
    return this._http
      .put<ResponseApi>(url, {}, { headers })
      .pipe(
        take(1),
        map((response) => {
          if (!response.ok) return null;
          return SetMapAutomobilePlatform(
            response.results as AutomobilePlatformApiModel
          );
        })
      );
  }

  deleteAutomobilePlatform(id: number): Observable<boolean> {
    const url = `${this.BASE_URL}automobile-platform/${id}`;
    const headers = new HttpHeaders();
    return this._http.delete<ResponseApi>(url, { headers }).pipe(
      take(1),
      map((response) => {
        return response.ok;
      })
    );
  }
}
